import type { Customer } from '../types';

// Dedicated system customer for anonymous walk-ins.
// We reuse this record to avoid polluting the customer list with one-off walk-in entries.
export const WALK_IN_SYSTEM_PHONE = '__walkin__';

/** Stored on the shared walk-in customer row created from the floor plan. */
export const WALK_IN_SYSTEM_CUSTOMER_NOTES = 'System customer for floor-plan walk-ins';

export function isWalkInSystemCustomer(
  customer: Pick<Customer, 'name' | 'phone'> | null | undefined,
): boolean {
  if (!customer) return false;
  const phone = (customer.phone || '').trim().toLowerCase();
  const name = (customer.name || '').trim().toLowerCase();
  return (
    phone === WALK_IN_SYSTEM_PHONE.toLowerCase() ||
    phone.startsWith('walkin-') || // legacy walk-in rows
    name === 'walk-in'
  );
}

/** Default reservation.notes written when seating a walk-in from the floor plan. */
export function isPlaceholderWalkInReservationNote(notes: string | null | undefined): boolean {
  return /^\s*walk-?in\s*$/i.test((notes ?? '').trim());
}

export function isPlaceholderWalkInCustomerNote(notes: string | null | undefined): boolean {
  return (notes ?? '').trim() === WALK_IN_SYSTEM_CUSTOMER_NOTES;
}

/** Non-empty reservation notes suitable to show in UI (hides system walk-in marker). */
export function getReservationNotesForDisplay(notes: string | null | undefined): string | null {
  const t = (notes ?? '').trim();
  if (!t) return null;
  if (isPlaceholderWalkInReservationNote(t)) return null;
  return t;
}

/** Non-empty customer notes suitable to show in UI (hides system walk-in customer text). */
export function getCustomerNotesForDisplay(notes: string | null | undefined): string | null {
  const t = (notes ?? '').trim();
  if (!t) return null;
  if (isPlaceholderWalkInCustomerNote(t)) return null;
  return t;
}

export type ReservationNotesPreviewInput = {
  notes?: string | null;
  customer?: Pick<Customer, 'notes'> | null;
};

export function reservationHasUserFacingNotes(r: ReservationNotesPreviewInput): boolean {
  return (
    getReservationNotesForDisplay(r.notes) != null ||
    getCustomerNotesForDisplay(r.customer?.notes) != null
  );
}

/** Real phone for detail views — null for the shared walk-in customer. */
export function getCustomerPhoneForDisplay(
  customer: Pick<Customer, 'name' | 'phone'> | null | undefined,
): string | null {
  if (!customer || isWalkInSystemCustomer(customer)) return null;
  const p = (customer.phone || '').trim();
  return p || null;
}

/**
 * Build a `tel:` href for the given display string (digits / + / separators).
 * Returns null when there are no dialable digits.
 */
export function getTelHrefForPhone(phone: string | null | undefined): string | null {
  if (!phone?.trim()) return null;
  const raw = phone.trim();
  if (raw.startsWith('+')) {
    const rest = raw.slice(1).replace(/\D/g, '');
    return rest.length > 0 ? `tel:+${rest}` : null;
  }
  const digits = raw.replace(/\D/g, '');
  return digits.length > 0 ? `tel:${digits}` : null;
}

/**
 * Second line under the guest name in reservation lists.
 * Walk-in system rows show an em dash instead of the sentinel phone.
 */
export function formatReservationListPhoneSubtitle(
  customer: Pick<Customer, 'name' | 'phone'> | null | undefined,
): string {
  if (!customer) return 'Χωρίς τηλέφωνο';
  if (isWalkInSystemCustomer(customer)) return '—';
  return (customer.phone || '').trim() || 'Χωρίς τηλέφωνο';
}
